// =============================================================================
// iTravel 2.0 — GuestGuard Component
// =============================================================================
// Wraps guest-only routes (login, register, forgot password, etc.).
// If the user is already authenticated, they are redirected to the home page.
// If auth state is still loading/hydrating, a loading indicator is shown.
// =============================================================================

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Box, CircularProgress } from '@mui/material';

import {
  selectAuthStatus,
  selectIsAuthenticated,
  selectIsHydrated,
  useAuthStore,
} from '@/store/auth.store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GuestGuardProps {
  /** Content to render when the user is NOT authenticated */
  children: ReactNode;
  /** Route to redirect to when the user IS authenticated (default: '/') */
  redirectTo?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * GuestGuard prevents authenticated users from accessing guest-only pages
 * like login and registration. If the user is already logged in, they are
 * redirected to the home page (or a custom redirect target).
 *
 * Usage:
 * ```tsx
 * <GuestGuard>
 *   <LoginPage />
 * </GuestGuard>
 * ```
 */
export function GuestGuard({ children, redirectTo = '/' }: GuestGuardProps) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isHydrated = useAuthStore(selectIsHydrated);
  const authStatus = useAuthStore(selectAuthStatus);
  const location = useLocation();

  // -------------------------------------------------------------------------
  // While the auth store is hydrating from persisted storage, show a loader.
  // This prevents a flash of the login page before redirect.
  // -------------------------------------------------------------------------
  if (!isHydrated || authStatus === 'idle') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // If authenticated, redirect away from the guest page.
  // Preserve any "from" location state so we can support deep-link redirects.
  // -------------------------------------------------------------------------
  if (isAuthenticated) {
    // Check if there's a "from" location the user was trying to reach
    // before being redirected to login. If so, send them there instead.
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    const target = from ?? redirectTo;

    return <Navigate to={target} replace />;
  }

  // -------------------------------------------------------------------------
  // Not authenticated — render the guest content (login, register, etc.)
  // -------------------------------------------------------------------------
  return <>{children}</>;
}

export default GuestGuard;
